import { ChangeEvent, FC, useMemo } from "react";

import { Grid } from "@mui/material";
import { FormType, OrganizationCountry } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../../common/constants/amplitude";
import { useLocaleContext } from "../../../../../../common/contexts/LocaleContext";
import { getDateInTimezone } from "../../../../../forms-design-system/FormDate/helper";
import { useFormV2Context } from "../../../contexts/FormV2Context";

import { DiscountCodeField } from "@/canvas/components/DiscountCodeField/DiscountCodeField";
import { findRateOrParent } from "@/common/helpers/rates";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";

export const TicketingRenewalAndDiscountsSection: FC = () => {
  const { locale } = useLocaleContext();
  const { t } = useTranslate();
  const { form, formCategory } = useFormV2Context(FormType.Ticketing);
  const {
    hasDiscountCodes,
    selectedOccurrenceId,
    membershipRenewalBuyerInfo,
    formObject: ticketing,
    validDiscount,
    setValidDiscount,
  } = useFormV2Context(FormType.Ticketing);
  const { logAmplitudeEventCallback } = useAmplitude();
  const isDiscountCodeGhostPreview = useIsGhostPreview("TicketingV2DiscountCode");

  const { expirationDate, uniquelySelectedTicketIndex, shouldProposeAutomaticRenewal } =
    membershipRenewalBuyerInfo || {};

  const ticketsPurchased = useWatch({ control: form.control, name: "ticketsPurchased" });
  const purchasedTickets = useMemo(
    () =>
      ticketsPurchased
        ?.filter((ticket) => (ticket.purchasedNumber || 0) > 0)
        .map((ticket) => {
          const rateOrParent = findRateOrParent(ticket.rateId, ticketing.rates ?? []);
          const parentRateId = rateOrParent && rateOrParent.id !== ticket.rateId ? rateOrParent.id : null;
          return {
            rateId: ticket.rateId,
            quantity: ticket.purchasedNumber || 0,
            isPayWhatYouCan: rateOrParent?.isPayWhatYouCan ?? false,
            isAddOn: rateOrParent?.isAddOn ?? false,
            parentRateId,
            attendeesByTicket: rateOrParent?.attendeesByTicket ?? null,
          };
        }),
    [ticketsPurchased, ticketing.rates]
  );

  const handleAutomaticRenewalChange = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    logAmplitudeEventCallback(AmplitudeEvents.TicketClickGeneralInput, {
      label: "automaticRenewal",
    });
    form.setValue(`ticketsPurchased.${uniquelySelectedTicketIndex || 0}.automaticRenewal`, checked);
  };

  const automaticRenewalChecked = form.watch(`ticketsPurchased.${uniquelySelectedTicketIndex || 0}.automaticRenewal`);

  if (!shouldProposeAutomaticRenewal && !hasDiscountCodes) {
    return null;
  }

  return (
    <>
      {shouldProposeAutomaticRenewal && (
        <Grid item marginBottom={2}>
          <FormCheckbox
            checked={automaticRenewalChecked}
            onChange={handleAutomaticRenewalChange}
            label={t("ticketing", "automaticRenewal", {
              expirationDate: expirationDate
                ? getDateInTimezone(expirationDate, ticketing.eventTimezone).toLocaleString(
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    },
                    { locale }
                  )
                : null,
            })}
            data-test="automatic-renewal-checkbox"
            size="medium"
            sx={{ display: "flex" }}
          />
        </Grid>
      )}

      {hasDiscountCodes && (
        <Grid
          item
          marginBottom={2}
          sx={{
            ...(isDiscountCodeGhostPreview && {
              opacity: GHOST_PREVIEW_OPACITY,
            }),
          }}
        >
          <DiscountCodeField
            setValue={form.setValue}
            register={form.register}
            discountIdName="discountId"
            ticketingId={ticketing.id}
            formCategory={formCategory}
            organizationCountry={ticketing.organization?.country as OrganizationCountry}
            selectedOccurrenceId={selectedOccurrenceId}
            purchasedTickets={purchasedTickets}
            validDiscount={validDiscount}
            onValidDiscountChange={setValidDiscount}
          />
        </Grid>
      )}
    </>
  );
};
